import { useEffect, useRef, useState } from 'react';
import SectionHead from '../ui/SectionHead.jsx';
import ProcessStep from '../primitives/ProcessStep.jsx';
import { processHead, processSteps } from '../../content/automatedSystemsProcess.js';

/**
 * Process — five-phase engagement flow.
 *
 * Layout: vertical spine with alternating left/right branches. Each phase
 * is anchored by a giant outlined numeral. A scroll-linked accent fill
 * descends the spine and the phase nearest the viewport centre is marked
 * active (numeral stroke flips to accent, spine dot glows + pulses).
 */
export default function Process() {
  const spineRef = useRef(null);
  const phaseRefs = useRef([]);
  const [progress, setProgress] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    let ticking = false;

    const update = () => {
      ticking = false;
      const spine = spineRef.current;
      if (!spine) return;

      const vh = window.innerHeight;
      const vpCenter = vh / 2;
      const sRect = spine.getBoundingClientRect();

      // Spine fill — 0 → 1 as the section passes viewport centre
      const total = Math.max(sRect.height, 1);
      const scrolled = Math.min(Math.max(vpCenter - sRect.top, 0), total);
      setProgress(scrolled / total);

      // Active phase — whichever centre is closest to viewport centre,
      // with an end-of-spine lock so the final phase activates once the
      // spine has scrolled fully above the viewport centre.
      let best = 0;
      let bestDist = Infinity;
      phaseRefs.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const centre = r.top + r.height / 2;
        const dist = Math.abs(centre - vpCenter);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      if (sRect.bottom < vpCenter) best = phaseRefs.current.length - 1;
      if (sRect.top > vpCenter) best = 0;
      setActiveIdx(best);
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <section
      id="process"
      className="section-pad bg-surface-alt text-ink-50 border-t border-white/[.06] relative overflow-hidden"
    >
      <div className="container-page">
        <SectionHead {...processHead} />

        <div ref={spineRef} className="relative py-4">
          {/* Static hairline spine — left-aligned on mobile, centred on md+ */}
          <div
            aria-hidden="true"
            className="absolute left-[20px] md:left-1/2 top-0 bottom-0 w-px md:-translate-x-1/2 bg-white/[.14]"
            style={{
              maskImage:
                'linear-gradient(180deg, transparent, #000 6%, #000 94%, transparent)',
              WebkitMaskImage:
                'linear-gradient(180deg, transparent, #000 6%, #000 94%, transparent)',
            }}
          />
          {/* Scroll-linked accent fill */}
          <div
            aria-hidden="true"
            className="absolute left-[20px] md:left-1/2 top-0 w-px h-full md:-translate-x-1/2 bg-bar-fill origin-top shadow-spine-glow"
            style={{
              transform: `scaleY(${progress})`,
              transition: 'transform 120ms linear',
            }}
          />

          {processSteps.map((step, i) => (
            <ProcessStep
              key={step.title}
              ref={(el) => {
                phaseRefs.current[i] = el;
              }}
              index={i + 1}
              side={i % 2 === 0 ? 'left' : 'right'}
              isActive={activeIdx === i}
              {...step}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
